import React, { useState, useEffect } from "react";
import { NamedPsrCaptureProfile } from "../../../Model/CaptureProfile";
import { PsrCameraCapabilities } from "../../../Model/PsrCapabilites";
import RadioButton from "../../Buttons/RadioButton";
import TextBox from "../../Common/TextBox";
import CommonDropDown from "../../Common/DropDown/CommonDropDown";
import "./RaspiCameraConfigurator.css";
import { useConfirm } from "../../Hooks/WindowHooks";

type Props = {
  psrCaptureProfile: NamedPsrCaptureProfile;
  psrCaptureProfiles: NamedPsrCaptureProfile[];
  setPsrCaptureProfile: (cp: NamedPsrCaptureProfile) => void;
  psrCameraCapabilities: PsrCameraCapabilities;
  isPsrCaptureProfileConfigUpdated: boolean;
  setIsPsrCaptureProfileConfigUpdated: (isPsrCpConfigUpdated: boolean) => void;
};

export default function RaspiCameraConfigurator({
  psrCaptureProfile,
  psrCaptureProfiles,
  setPsrCaptureProfile,
  psrCameraCapabilities,
  isPsrCaptureProfileConfigUpdated,
  setIsPsrCaptureProfileConfigUpdated,
}: Props) {
  const binningDisplayValues = psrCameraCapabilities.binning
    ? psrCameraCapabilities.binning.binningDisplayValues.map(
        (binningDisplayValue) => {
          const { binning, resolution, resolutionUnit } = binningDisplayValue;
          return {
            display: `${binning}.${binning} binning`,
            value: binning,
            disabled: false,
          };
        }
      )
    : [];

  const psrCaptureProfileDisplayValues = psrCaptureProfiles.map((psrCp) => {
    return {
      display: psrCp.captureProfileName,
      value: psrCp.id,
      disabled: false,
    };
  });
  const userConfirmChangePsrCaptureProfile = useConfirm(
    "Changes are not saved, are you sure you want to Change Psr Capture Profile?"
  );
  userConfirmChangePsrCaptureProfile.setOnCancel(() => {
    return;
  });

  const onChangeSelectedPsrCaptureProfile = (id: number) => {
    if (isPsrCaptureProfileConfigUpdated && id !== psrCaptureProfile.id) {
      userConfirmChangePsrCaptureProfile.setOnOk(() => {
        const selectedPsrCp = psrCaptureProfiles.find(
          (psrCp) => psrCp.id === id
        );
        if (selectedPsrCp) {
          setPsrCaptureProfile(selectedPsrCp);
          setIsPsrCaptureProfileConfigUpdated(false);
        }
      });
      userConfirmChangePsrCaptureProfile.showConfirmationAlert();
    } else {
      const selectedPsrCp = psrCaptureProfiles.find((psrCp) => psrCp.id === id);
      if (selectedPsrCp) {
        setPsrCaptureProfile(selectedPsrCp);
      }
    }
  };

  const focus = psrCameraCapabilities.focus;
  const gain = psrCameraCapabilities.gain;
  const brightness = psrCameraCapabilities.brightness;
  const exposure = psrCameraCapabilities.exposure;
  const binning = psrCameraCapabilities.binning;
  const autoFocusAlgo = psrCaptureProfile.autoFocus;
  const autoExposureAlgo = psrCaptureProfile.autoExposure;

  let configs = [];

  configs.push(<div className="psr-camera-conf-title">Capture Profile</div>);
  configs.push(
    <div className="psr-camera-conf-item" style={{width: "100%"}}>
      <CommonDropDown
        dropDownDisplayValues={psrCaptureProfileDisplayValues}
        id="psr-capture-profile"
        value={psrCaptureProfile.id}
        onChange={(value) => onChangeSelectedPsrCaptureProfile(value)}
        style={{
          width: "100%",
          height: 34,
          fontSize: 13,
        }}
      />
    </div>
  );

  if (focus || autoFocusAlgo) {
    configs.push(<div className="psr-camera-conf-title">Focus Option</div>);
  }

  if (autoFocusAlgo) {
    configs.push(
      <div className="psr-camera-conf-item" style={{ marginBottom: 8.5 }}>
        <RadioButton
          id="auto-focus"
          name="auto-focus"
          label="Auto Focus"
          color="default"
          checked={psrCaptureProfile.autoFocus?.useAutoFocus ?? false}
          onChange={() => {
            setPsrCaptureProfile({
              ...psrCaptureProfile,
              autoFocus: { ...psrCaptureProfile.autoFocus, useAutoFocus: true },
            });
            setIsPsrCaptureProfileConfigUpdated(true);
          }}
          style={{
            fontSize: 14,
          }}
        />
      </div>
    );
  }

  if (focus) {
    configs.push(
      <div className="psr-camera-conf-item">
        <RadioButton
          id="manual-focus"
          name="manual-focus"
          label="Manual Focus"
          color="default"
          checked={!psrCaptureProfile.autoFocus?.useAutoFocus ?? true}
          onChange={() => {
            if (autoFocusAlgo) {
              setPsrCaptureProfile({
                ...psrCaptureProfile,
                autoFocus: {
                  ...psrCaptureProfile.autoFocus,
                  useAutoFocus: false,
                },
              });
              setIsPsrCaptureProfileConfigUpdated(true);
            }
          }}
          style={{
            fontSize: 14,
          }}
        />
        <div className="psr-camera-conf-value">
          <TextBox
            name="manual-focus-input"
            value={psrCaptureProfile.focus ?? 0}
            decimalPoint={2}
            isInteger={false}
            variant={"number"}
            onClick={() => {
              if (autoFocusAlgo) {
                setPsrCaptureProfile({
                  ...psrCaptureProfile,
                  autoFocus: {
                    ...psrCaptureProfile.autoFocus,
                    useAutoFocus: false,
                  },
                });
                setIsPsrCaptureProfileConfigUpdated(true);
              }
            }}
            onChange={(value) => {
              setPsrCaptureProfile({
                ...psrCaptureProfile,
                focus: value as number,
              });
              setIsPsrCaptureProfileConfigUpdated(true);
            }}
          />
          <div className="psr-camera-conf-unit">x</div>
        </div>
      </div>
    );
  }

  if (exposure || autoExposureAlgo) {
    configs.push(<div className="psr-camera-conf-title">Exposure Option</div>);
  }

  if (autoExposureAlgo) {
    configs.push(
      <div className="psr-camera-conf-item" style={{ marginBottom: 8.5 }}>
        <RadioButton
          id="auto-exposure"
          name="auto-exposure"
          label="Auto Exposure"
          color="default"
          checked={psrCaptureProfile.autoExposure?.useAutoExposure ?? false}
          onChange={() => {
            setPsrCaptureProfile({
              ...psrCaptureProfile,
              autoExposure: {
                ...psrCaptureProfile.autoExposure,
                useAutoExposure: true,
              },
            });
            setIsPsrCaptureProfileConfigUpdated(true);
          }}
          style={{
            fontSize: 14,
          }}
        />
      </div>
    );
  }

  if (exposure) {
    configs.push(
      <div className="psr-camera-conf-item">
        <RadioButton
          id="fixed-exposure"
          name="fixed-exposure"
          label="Fixed Exposure"
          color="default"
          checked={!psrCaptureProfile.autoExposure?.useAutoExposure ?? true}
          onChange={() => {
            if (autoExposureAlgo) {
              setPsrCaptureProfile({
                ...psrCaptureProfile,
                autoExposure: {
                  ...psrCaptureProfile.autoExposure,
                  useAutoExposure: false,
                },
              });
              setIsPsrCaptureProfileConfigUpdated(true);
            }
          }}
          style={{
            fontSize: 14,
          }}
        />
        <div className="psr-camera-conf-value">
          <TextBox
            name="fixed-exposure-input"
            value={psrCaptureProfile.exposure ?? 0}
            decimalPoint={2}
            isInteger={false}
            variant={"number"}
            onClick={() => {
              if (autoExposureAlgo) {
                setPsrCaptureProfile({
                  ...psrCaptureProfile,
                  autoExposure: {
                    ...psrCaptureProfile.autoExposure,
                    useAutoExposure: false,
                  },
                });
                setIsPsrCaptureProfileConfigUpdated(true);
              }
            }}
            onChange={(value) => {
              setPsrCaptureProfile({
                ...psrCaptureProfile,
                exposure: value as number,
              });
              setIsPsrCaptureProfileConfigUpdated(true);
            }}
          />
          <div className="psr-camera-conf-unit">ms</div>
        </div>
      </div>
    );
  }

  if (gain && false) {
    /// remove gain, because the backend is not ready
    configs.push(
      <div className="psr-camera-conf-item">
        <div className="psr-camera-conf-label">Gain</div>
        <div className="psr-camera-conf-value">
          <TextBox
            name="gain-input"
            value={psrCaptureProfile.gain ?? 0}
            decimalPoint={2}
            isInteger={false}
            variant={"number"}
            onChange={(value) => {
              setPsrCaptureProfile({
                ...psrCaptureProfile,
                gain: value as number,
              });
              setIsPsrCaptureProfileConfigUpdated(true);
            }}
          />
          <div className="psr-camera-conf-unit">dB</div>
        </div>
      </div>
    );
  }

  if (binning) {
    configs.push(
      <div className="psr-camera-conf-item">
        <div className="psr-camera-conf-label">Binning</div>
        <div className="psr-camera-conf-value">
          <CommonDropDown
            id="psr-cam-conf-binning"
            value={psrCaptureProfile.binning ?? 0}
            onChange={(value) => {
              setPsrCaptureProfile({ ...psrCaptureProfile, binning: value });
              setIsPsrCaptureProfileConfigUpdated(true);
            }}
            dropDownDisplayValues={binningDisplayValues}
            style={{ width: 125.58 }}
          />
          <div className="psr-camera-conf-unit"> </div>
        </div>
      </div>
    );
  }

  if (brightness) {
    configs.push(
      <div className="psr-camera-conf-item">
        <div className="psr-camera-conf-label">Bright Field</div>
        <div className="psr-camera-conf-value">
          <TextBox
            name="brightness"
            value={psrCaptureProfile.brightness ?? 0}
            decimalPoint={2}
            isInteger={false}
            variant={"number"}
            onChange={(value) => {
              setPsrCaptureProfile({
                ...psrCaptureProfile,
                brightness: value as number,
              });
              setIsPsrCaptureProfileConfigUpdated(true);
            }}
          />
          <div className="psr-camera-conf-unit">%</div>
        </div>
      </div>
    );
  }

  return <div className="psr-camera-configurator">{configs}</div>;
}
